// In-game heads-up display (DOM overlay projected from the 3D scene).
import { RECIPES, INGREDIENTS, PERKS, CHEF_COLORS, SPECTATOR_COLOR, clockText } from '../game/data.js';
import { input, soloHints } from '../engine/input.js';
import { delegateTasks } from '../game/helper.js';

/** A ticket rim's conic-gradient stops: an even arc of light per colour, each brightening to white at its heart. */
function rimStops(cols) {
  const w = 360 / cols.length;
  return cols.map((c, i) => {
    const at = (f) => (i * w + f * w).toFixed(1) + 'deg';
    return `transparent ${at(0)}, ${c} ${at(0.28)}, #fff ${at(0.39)}, ${c} ${at(0.5)}, transparent ${at(0.78)}, transparent ${at(1)}`;
  }).join(', ');
}

const $ = (sel, root = document) => root.querySelector(sel);
const h = (tag, cls, html) => {
  const e = document.createElement(tag);
  if (cls) e.className = cls;
  if (html !== undefined) e.innerHTML = html;
  return e;
};

const TOOL_KNIFE = `<svg class="tool" viewBox="0 0 32 20"><path class="blade" d="M2 5 C8 13 18 14 21 13 L21 6 Z"/><path class="edge" d="M2 5 C8 13 18 14 21 13"/><rect class="handle" x="20.5" y="6.5" width="10" height="5" rx="2.2"/><circle class="rivet" cx="24" cy="9" r=".9"/><circle class="rivet" cx="27.5" cy="9" r=".9"/></svg>`;
const TOOL_MORTAR = `<svg class="tool" viewBox="0 0 32 20"><rect class="handle" x="15" y="0.8" width="4.2" height="12" rx="2" transform="rotate(28 17 7)"/><path class="blade" d="M5 9 H27 C27 15 22 19 16 19 C10 19 5 15 5 9 Z"/><path class="edge" d="M4 9 H28"/></svg>`;

export class HUD {
  constructor(app) {
    this.app = app;
    this.root = $('#hud');
    this.markers = new Map();
    this.used = new Set();
    this.tickets = new Map();
    this._p = { x: 0, y: 0, visible: true };
  }
  icon(key) {
    return this.app.icons[key] || '';
  }
  img(key, cls = 'ic') {
    return `<img class="${cls}" src="${this.icon(key)}" alt="">`;
  }
  /** A Delegate task's glyph: an emoji, or one of the rendered icons. */
  glyph(gl, cls = 'ic') {
    return gl.startsWith('img:') ? this.img(gl.slice(4), cls) : gl;
  }
  /** Ingredient with all its stages: raw → (tool) → prepared. */
  ingStages(id) {
    const d = INGREDIENTS[id];
    if (!d.prep) return `<span class="stg">${this.img('ing:' + id + ':raw', 'ic')}</span>`;
    const tool = d.prep === 'chop' ? TOOL_KNIFE : TOOL_MORTAR;
    const verb = d.prep === 'chop' ? 'Chop' : 'Grind';
    return `<span class="stg multi" title="${verb} the ${d.name}">${this.img('ing:' + id + ':raw', 'ic')}<span class="tr ${d.prep}">${tool}<svg class="arr" viewBox="0 0 24 10"><path d="M1 5h18"/><path d="M15 1l6 4-6 4"/></svg></span>${this.img('ing:' + id + ':' + (d.prep === 'chop' ? 'chopped' : 'ground'), 'ic')}</span>`;
  }
  /** Boba finishing steps: cup with pearls → Sealer → straw. */
  bobaSteps() {
    const arr = '<svg class="arr" viewBox="0 0 24 10"><path d="M1 5h18"/><path d="M15 1l6 4-6 4"/></svg>';
    const step = (key, label) => `<span class="bs" title="${label}">${this.img(key, 'ic')}<em>${label}</em></span>`;
    return `<div class="tk-boba">${step('bobacup', 'Pearls')}${arr}${step('sealer', 'Seal')}${arr}${step('straw', 'Straw')}</div>`;
  }
  ingIcon(id) {
    const d = INGREDIENTS[id];
    const st = d.prep === 'chop' ? 'chopped' : d.prep === 'grind' ? 'ground' : 'raw';
    return this.img('ing:' + id + ':' + st);
  }

  startLevel(game) {
    this.game = game;
    const L = game.level;
    this.root.innerHTML = '';
    this.tut = null;
    this.dgBar = null;
    this._dgSig = null;
    this.root.classList.remove('hidden');
    this.markers.clear();
    this.tickets.clear();
    this._tkScale = null;
    this.orders = h('div', 'orders');
    this.root.appendChild(this.orders);
    this.markerLayer = h('div', 'markers');
    this.root.appendChild(this.markerLayer);
    this.timer = h('div', 'timer', `<svg viewBox="0 0 100 100"><circle class="bg" cx="50" cy="50" r="44"/><circle class="fg" cx="50" cy="50" r="44"/></svg><div class="t">0:00</div>`);
    this.root.appendChild(this.timer);
    this.score = h('div', 'score', `<div class="coin"></div><div class="val">0</div><div class="streak"></div>`);
    this.root.appendChild(this.score);
    this.perkBar = h('div', 'perkbar');
    this.root.appendChild(this.perkBar);
    this.banners = h('div', 'banners');
    this.root.appendChild(this.banners);
    this.pops = h('div', 'pops');
    this.root.appendChild(this.pops);
    // intro card
    const idx = game.levelIndex + 1;
    const intro = h('div', 'level-intro', `
      <div class="li-num">Shift ${idx}</div>
      <div class="li-name">${L.name}</div>
      <div class="li-sub">${L.sub}</div>
      <div class="li-twist">${L.twistText}</div>
      <div class="li-menu">${L.recipes.map((r) => `<span>${this.img('drink:' + r)}${RECIPES[r].name}</span>`).join('')}</div>`);
    this.root.appendChild(intro);
    setTimeout(() => intro.classList.add('out'), 2600);
    setTimeout(() => intro.remove(), 3400);
    // controls hint
    this.hint = h('div', 'controls-hint', this._hintHtml(game));
    this._hintDev = input.lastDevice;
    this.root.appendChild(this.hint);
    this._placeToasts();
    setTimeout(() => { if (this.hint) { this.hint.classList.add('fade'); this._placeToasts(); } }, 14000);
    this._lastCoins = -1;
  }
  _hintHtml(game) {
    const sep = '<span class="sep">·</span>';
    const bots = game.chefs.some((c) => c.isBot);
    // online: only the people on this screen (and bots need no reminding)
    const here = (p) => !p.device.startsWith('net:') && !p.device.startsWith('bot');
    const cooks = game.players.map((pl, i) => {
      if (!here(pl)) return '';
      const hi = this._hintsFor(game, pl);
      return `<div class="ch" style="--c:${CHEF_COLORS[i]}"><b>${game.solo ? 'You' : 'P' + (i + 1)}</b> ${hi.move} move ${sep} ${hi.grab} grab / place / give ${sep} ${hi.use} chop / wash / throw ${sep} ${hi.dash} dash${bots ? ` ${sep} ${hi.swap} delegate` : ''}</div>`;
    });
    // a spectator's only button in a shift is Delegate, for their share of the bots
    const watchers = game.spectators.filter(here).map((pl) => `<div class="ch" style="--c:${SPECTATOR_COLOR}"><b>Spectating</b> ${bots ? `${this._hintsFor(game, pl).swap} delegate` : 'the crew at work'}</div>`);
    return [...cooks, ...watchers].join('');
  }
  _hintsFor(game, pl) {
    return game.solo ? soloHints() : input.hintsFor(pl.device);
  }
  /** Delegate wheel: hold the key, aim at a task, release to switch it on or off. */
  _wheelHtml(game, w) {
    const n = w.tasks.length, S = 250, C = S / 2, R = 116, r = 46, gap = n > 1 ? 0.035 : 0;
    const pt = (a, d) => `${(C + Math.sin(a) * d).toFixed(1)} ${(C - Math.cos(a) * d).toFixed(1)}`;
    const step = (Math.PI * 2) / n;
    let segs = '', glyphs = '';
    w.tasks.forEach((t, i) => {
      const on = !!game.prefsOf(w.owner)[t.id], sel = w.sel === i;
      const cls = 'wseg' + (on ? ' on' : '') + (sel ? ' sel' : '');
      if (n === 1) segs += `<circle class="${cls}" cx="${C}" cy="${C}" r="${(R + r) / 2}" style="stroke-width:${R - r}px"/>`;
      else {
        const a0 = (i - 0.5) * step + gap, a1 = (i + 0.5) * step - gap, big = a1 - a0 > Math.PI ? 1 : 0;
        segs += `<path class="${cls}" d="M${pt(a0, R)} A${R} ${R} 0 ${big} 1 ${pt(a1, R)} L${pt(a1, r)} A${r} ${r} 0 ${big} 0 ${pt(a0, r)} Z"/>`;
      }
      const a = i * step, d = (R + r) / 2;
      glyphs += `<div class="wg${on ? ' on' : ''}${sel ? ' sel' : ''}" style="left:${(C + Math.sin(a) * d).toFixed(1)}px;top:${(C - Math.cos(a) * d).toFixed(1)}px"><span>${this.glyph(t.glyph)}</span><em>${t.name}</em></div>`;
    });
    const t = w.sel != null ? w.tasks[w.sel] : null;
    const mid = t
      ? `<b>${t.name}</b><small>${game.prefsOf(w.owner)[t.id] ? 'On: release to stop' : 'Release to start'}</small>`
      : '<b>Delegate</b><small>Toggle behaviors</small>';
    return `<div class="wheel"><svg viewBox="0 0 ${S} ${S}">${segs}<circle class="whub" cx="${C}" cy="${C}" r="${r - 5}"/></svg>${glyphs}<div class="wmid">${mid}</div></div>`;
  }
  /** Aimed and switched-on segments (updated in place, so the wheel doesn't pop in again). */
  _wheelState(el, game, w) {
    const segs = el.querySelectorAll('.wseg'), gl = el.querySelectorAll('.wg');
    w.tasks.forEach((t, i) => {
      const on = !!game.prefsOf(w.owner)[t.id], sel = w.sel === i;
      for (const e of [segs[i], gl[i]]) {
        e.classList.toggle('on', on);
        e.classList.toggle('sel', sel);
      }
    });
    const t = w.sel != null ? w.tasks[w.sel] : null;
    const mid = t ? `<b>${t.name}</b><small>${game.prefsOf(w.owner)[t.id] ? 'On: let go to stop' : 'Let go to start'}</small>` : '<b>Delegate</b><small>Toggle behaviors</small>';
    const m = el.querySelector('.wmid');
    if (m._html !== mid) m.innerHTML = m._html = mid;
  }
  /** A bot's little thought bubble: what it's busy with (dots while it has nothing to do). */
  _thoughtHtml(c) {
    const th = c.thought || '';
    const one = (x) => (x.startsWith('img:') ? this.img(x.slice(4)) : `<span class="em">${x}</span>`);
    const parts = th ? th.split('|') : [];
    const inner = parts.length ? parts.map(one).join('<i class="to"></i>') : '<span class="dots"><i></i><i></i><i></i></span>';
    return `<div class="mk-think" style="--c:${c.color}"><div class="tk"><i class="tb3"></i><i class="tb2"></i><div class="tb1${parts.length > 1 ? ' multi' : ''}">${inner}</div></div></div>`;
  }
  /** Which Delegate tasks are on (only when there's a bot in the crew). */
  _delegateBar(game) {
    const bots = game.chefs.some((c) => c.isBot);
    const tasks = bots ? delegateTasks(game) : [];
    const me = [...game.players, ...game.spectators].find((p) => !p.device.startsWith('net:') && !p.device.startsWith('bot'));
    const key = me ? this._hintsFor(game, me).swap : '';
    // this screen's (first) person's own choices, for their own bots (cooking or watching)
    const owner = me ? game.ownerKey(me) : null, prefs = game.prefsOf(owner), mine = owner ? game.ownedBots(owner).length : 0;
    const sig = tasks.map((t) => t.id + (prefs[t.id] ? 1 : 0)).join() + '|' + bots + key + mine;
    if (this._dgSig === sig) return;
    this._dgSig = sig;
    if (!bots) {
      if (this.dgBar) this.dgBar.remove();
      this.dgBar = null;
      return;
    }
    if (!this.dgBar || !this.dgBar.isConnected) {
      this.dgBar = h('div', 'dg-bar');
      this.root.appendChild(this.dgBar);
    }
    const glyphs = mine ? tasks.map((t) => `<span class="dg${prefs[t.id] ? ' on' : ''}" title="${t.name}: ${t.desc}">${this.glyph(t.glyph)}</span>`).join('') : '';
    const count = game.solo ? '' : `<span class="dg-n">${mine ? mine + (mine > 1 ? ' bots' : ' bot') : 'no bots'}</span>`;
    this.dgBar.innerHTML = `${key}<span class="dg-l">Delegate</span>${count}${glyphs}`;
  }
  /** Reactive coach bar: one line per active chef. */
  _coach(game) {
    if (!this.tut) {
      this.tut = h('div', 'coach');
      this.root.appendChild(this.tut);
    }
    const rows = game.chefs.filter((c) => c.hint && c.active);
    const sig = rows.map((c) => c.index + c.hint.text).join('|');
    if (this.tut.dataset.sig === sig) return;
    this.tut.dataset.sig = sig;
    const multi = !game.solo;
    this.tut.innerHTML = rows
      .map((c) => `<div class="coach-row" style="--c:${c.color}">${multi ? `<span class="who">P${c.index + 1}</span>` : '<span class="who">★</span>'}<span class="txt">${c.hint.text}</span></div>`)
      .join('');
    this.tut.classList.remove('flash');
    void this.tut.offsetWidth;
    this.tut.classList.add('flash');
    this._placeToasts();
  }
  hide() {
    this.tut = null;
    this.dgBar = null;
    this._dgSig = null;
    this.root.classList.add('hidden');
    this.root.innerHTML = '';
  }
  countdownStep(i) {
    const seq = ['3', '2', '1', 'Serve!'];
    const e = h('div', 'countdown' + (i === 3 ? ' go' : ''), seq[i]);
    this.root.appendChild(e);
    this.app.audio.sfx('countdown', { go: i === 3 });
    setTimeout(() => e.remove(), 900);
  }
  timeUp() {
    const e = h('div', 'countdown go timeup', 'Closing Time!');
    this.root.appendChild(e);
    setTimeout(() => e.remove(), 2400);
  }
  banner(title, sub, color) {
    const e = h('div', 'banner', `<div class="bt">${title}</div><div class="bs">${sub || ''}</div>`);
    e.style.setProperty('--c', color || '#fff');
    this.banners.appendChild(e);
    setTimeout(() => e.classList.add('out'), 2600);
    setTimeout(() => e.remove(), 3200);
  }
  /** Big celebratory card when a drink unlocks a crew bonus. Timed bonuses fly into the perk bar. */
  perkReveal(key, recipeId, result) {
    const P = PERKS[key];
    if (!P) return;
    if (!this.toasts || !this.toasts.isConnected) {
      this.toasts = h('div', 'toasts');
      this.root.appendChild(this.toasts);
    }
    this._placeToasts();
    const conf = Array.from({ length: 14 }, (_, i) => `<i style="--a:${(i / 14) * 360}deg;--d:${(0.15 + (i % 3) * 0.08).toFixed(2)}s"></i>`).join('');
    const e = h('div', 'perk-toast', `
      <div class="pt-burst">${conf}</div>
      <div class="pt-medal"><span>${P.icon || '★'}</span></div>
      <div class="pt-body">
        <div class="pt-top"><span class="pt-kick">Crew bonus</span><b class="pt-name">${P.name}</b>${recipeId && RECIPES[recipeId] ? `<span class="pt-from">${this.img('drink:' + recipeId, 'ic')}${RECIPES[recipeId].name}</span>` : ''}</div>
        <div class="pt-desc">${P.desc}${result ? ` <span class="pt-result">✓ ${result}</span>` : ''}</div>
        ${P.dur ? `<div class="pt-dur"><i style="animation-duration:${P.dur}s"></i></div>` : ''}
      </div>`);
    e.style.setProperty('--c', P.color);
    this.toasts.appendChild(e);
    while (this.toasts.children.length > 3) this.toasts.firstChild.remove();
    setTimeout(() => e.classList.add('out'), 4200);
    setTimeout(() => e.remove(), 4800);
  }
  /** Bonus toasts sit at the bottom, just above the coach tips when those are showing. */
  _placeToasts() {
    // bottom stack, from the bottom up: coach tips, controls reminder, bonus toasts
    const ch = this.tut && this.tut.isConnected && this.tut.childElementCount ? this.tut.offsetHeight : 0;
    let base = 16 + (ch ? ch + 10 : 0);
    if (this.hint && this.hint.isConnected) {
      this.hint.style.bottom = base + 'px';
      if (!this.hint.classList.contains('fade')) base += this.hint.offsetHeight + 8;
    }
    if (this.toasts) this.toasts.style.bottom = base + 'px';
  }
  popText(html, pos, color, big) {
    const e = h('div', 'pop' + (big ? ' big' : ''), html);
    e.style.color = color || '#fff';
    const p = this.game.camera.project(pos, innerWidth, innerHeight);
    e.style.left = p.x + 'px';
    e.style.top = p.y + 'px';
    this.pops.appendChild(e);
    setTimeout(() => e.remove(), 1400);
  }
  bumpScore(mul, bad) {
    this.score.classList.remove('bump', 'bad');
    void this.score.offsetWidth;
    this.score.classList.add(bad ? 'bad' : 'bump');
  }
  orderNew(g) {
    const r = g.recipe;
    const t = h('div', 'ticket in', `
      <div class="tk-top" style="--c:${g.species.color};--dc:${r.color}">
        ${this.img('drink:' + r.id, 'ic big')}
        <div class="tk-name">${r.name}</div>
      </div>
      <div class="tk-ing">${r.ing.map((i) => this.ingStages(i)).join('')}<span class="tk-app">${this.img(r.app === 'kettle' ? 'kettle' : 'blender', 'ic app')}${r.fizz ? this.img('fizz', 'ic app') : ''}</span></div>
      ${r.boba ? this.bobaSteps() : ''}
      ${r.perk ? `<div class="tk-perk" title="${PERKS[r.perk].desc}" style="--c:${PERKS[r.perk].color}">★ ${PERKS[r.perk].name}: <span>${PERKS[r.perk].short}</span></div>` : ''}
      <div class="tk-bar"><div></div></div>
      <div class="tk-rim"></div>
      <div class="tk-seat" style="background:${g.species.color}">${g.species.name}</div>`);
    // the ticket floats above the guest who ordered it (anchor = screen position, pos = centring + fit scale)
    const anchor = h('div', 'tk-anchor');
    const pos = h('div', 'tk-pos');
    pos.appendChild(t);
    anchor.appendChild(pos);
    this.orders.appendChild(anchor);
    t._anchor = anchor;
    t._pos = pos;
    t._guest = g;
    t._h = t.offsetHeight || 160;
    t._w = t.offsetWidth || 200;
    this._layoutTickets(this.game, 0);
    requestAnimationFrame(() => t.classList.remove('in'));
    this.tickets.set(g.id, t);
  }
  orderDone(g, ok) {
    const t = this.tickets.get(g.id);
    if (!t) return;
    t.classList.add(ok ? 'ok' : 'fail');
    this.tickets.delete(g.id);
    setTimeout(() => (t._anchor || t).remove(), 650);
  }

  // ------------------------------------------------------------ markers
  /**
   * Lay out all order tickets above their guests: one shared scale (eased, never jumping) so they
   * fit side by side, pushed apart so they never overlap, and kept fully on screen (name tags included).
   */
  _layoutTickets(game, dt, instant = false) {
    const live = [...this.tickets.values()];
    if (!live.length) return;
    const PAD = 6, TAG = 12; // gap between tickets; name-tag overhang above/right
    const items = live.map((t) => {
      const p = game.camera.project([t._guest.x, t._guest.y + 1.35, t._guest.z], innerWidth, innerHeight, this._p);
      return { t, px: p.x, py: p.y, w: (t._w || 200) + 8, h: (t._h || 160) + 12 + TAG };
    }).sort((a, b) => a.px - b.px);
    // scale needed so neighbours don't overlap and the whole row fits across the screen
    let fit = 1;
    for (let i = 1; i < items.length; i++) fit = Math.min(fit, (items[i].px - items[i - 1].px - PAD) / ((items[i].w + items[i - 1].w) / 2));
    const total = items.reduce((a, it) => a + it.w, 0) + PAD * (items.length - 1);
    fit = Math.min(fit, (innerWidth - 24 - 110) / total);
    const target = Math.max(0.55, Math.min(1, fit));
    this._tkScale = instant || this._tkScale == null ? target : this._tkScale + (target - this._tkScale) * (1 - Math.exp(-5 * dt));
    const s = this._tkScale;
    // horizontal: start above each guest, then push apart and back inside the screen
    const L = 10, R = innerWidth - 10;
    items.forEach((it) => { it.x = it.px; it.W = it.w * s; it.H = it.h * s; });
    for (let i = 1; i < items.length; i++) items[i].x = Math.max(items[i].x, items[i - 1].x + (items[i - 1].W + items[i].W) / 2 + PAD);
    for (let i = items.length - 1; i >= 0; i--) {
      const it = items[i];
      // keep clear of the shift timer when the ticket reaches the top-right corner
      const right = it.py - it.H < 120 ? R - 110 : R;
      const max = i === items.length - 1 ? right - it.W / 2 : Math.min(right - it.W / 2, items[i + 1].x - (items[i + 1].W + it.W) / 2 - PAD);
      it.x = Math.min(it.x, max);
    }
    for (let i = 0; i < items.length; i++) {
      const min = i === 0 ? L + it0(items[i]) : items[i - 1].x + (items[i - 1].W + items[i].W) / 2 + PAD;
      items[i].x = Math.max(items[i].x, min);
    }
    function it0(it) { return it.W / 2; }
    for (const it of items) {
      const y = Math.max(it.H + 4, it.py);
      it.t._anchor.style.transform = `translate(${it.x.toFixed(1)}px, ${y.toFixed(1)}px)`;
      if (Math.abs((it.t._scale || 0) - s) > 0.002) {
        it.t._pos.style.transform = `translate(-50%, -100%) scale(${s.toFixed(3)})`;
        it.t._scale = s;
      }
    }
  }
  _mark(key, pos, sig, build) {
    let m = this.markers.get(key);
    if (!m) {
      m = { el: h('div', 'mk'), sig: null };
      this.markerLayer.appendChild(m.el);
      this.markers.set(key, m);
    }
    if (m.sig !== sig) {
      m.el.innerHTML = build();
      m.sig = sig;
    }
    const p = this.game.camera.project(pos, innerWidth, innerHeight, this._p);
    m.el.style.transform = `translate(${p.x.toFixed(1)}px, ${p.y.toFixed(1)}px)`;
    m.el.style.display = p.visible ? '' : 'none';
    this.used.add(key);
    return m.el;
  }
  /** Generic world-space progress bar with a label. */
  _prog(key, pos, frac, cls, label) {
    let m = this.markers.get(key);
    const sig = cls + label;
    if (!m || m.sig !== sig) {
      if (!m) {
        m = { el: h('div', 'mk'), sig: null };
        this.markerLayer.appendChild(m.el);
        this.markers.set(key, m);
      }
      m.el.innerHTML = `<div class="mk-pbar ${cls}"><span>${label}</span><div class="trk"><i></i></div></div>`;
      m.fill = m.el.querySelector('.trk i');
      m.sig = sig;
    }
    m.fill.style.transform = `scaleX(${Math.max(0, Math.min(1, frac)).toFixed(3)})`;
    const p = this.game.camera.project(pos, innerWidth, innerHeight, this._p);
    m.el.style.transform = `translate(${p.x.toFixed(1)}px, ${p.y.toFixed(1)}px)`;
    m.el.style.display = p.visible ? '' : 'none';
    this.used.add(key);
    return m.el;
  }
  update(game, dt) {
    // controls line follows whatever the solo player last touched (keyboard / controller)
    if (this.hint && this._hintDev !== input.lastDevice) {
      this._hintDev = input.lastDevice;
      this.hint.innerHTML = this._hintHtml(game);
      this._placeToasts();
      if (game.solo) {
        this.hint.classList.remove('fade');
        clearTimeout(this._hintFade);
        this._hintFade = setTimeout(() => { if (this.hint) { this.hint.classList.add('fade'); this._placeToasts(); } }, 8000);
      }
    }
    // timer: a timed shift counts down; an infinite one counts its time played up, its ring sweeping once a minute
    const tl = Math.max(0, game.timeLeft);
    $('.t', this.timer).textContent = clockText(game.infinite ? game.played : tl);
    const frac = game.infinite ? (game.played % 60) / 60 : tl / game.level.time;
    const fg = $('.fg', this.timer);
    fg.style.strokeDashoffset = String(276.5 * (1 - frac));
    this.timer.classList.toggle('hurry', !game.infinite && tl <= 30 && game.state === 'play');
    this.timer.classList.toggle('infinite', !!game.infinite);
    this.timer.classList.toggle('long', !!game.infinite && game.played >= 3600); // h:mm:ss needs a smaller face
    // score
    if (this._lastCoins !== game.coins) {
      $('.val', this.score).textContent = game.coins;
      this._lastCoins = game.coins;
    }
    const mul = game.streakMul();
    $('.streak', this.score).innerHTML = mul > 1 ? `Tip x${mul}` : '';
    // perks
    const act = Object.entries(game.perks).filter(([, v]) => v > 0);
    const sig = act.map(([k, v]) => k + Math.ceil(v)).join();
    if (sig !== this._perkSig) {
      this._perkSig = sig;
      this.perkBar.innerHTML = act.map(([k, v]) => `<div class="perk" style="--c:${PERKS[k].color}"><span class="pk-ic">${PERKS[k].icon || '★'}</span><span class="pk-t">${PERKS[k].name}<small>${PERKS[k].short}</small></span> <b>${Math.ceil(v)}s</b><i style="width:${((v / (PERKS[k].dur || 1)) * 100).toFixed(1)}%"></i></div>`).join('');
    }
    // tickets follow their guests (shared, eased scale; never overlapping; always on screen)
    this._layoutTickets(game, dt);
    // tickets patience
    const own = (c) => (c.local && !c.isBot ? 0 : 1);
    const carriers = [...game.deliveries().keys()].sort((a, b) => own(a) - own(b));
    for (const g of game.guests) {
      const t = this.tickets.get(g.id);
      if (!t) continue;
      const f = g.frac;
      const bar = t.querySelector('.tk-bar div');
      bar.style.width = (f * 100).toFixed(1) + '%';
      bar.style.background = f > 0.5 ? '#7dff9a' : f > 0.25 ? '#ffd35a' : '#ff5a5a';
      t.classList.toggle('urgent', f < 0.25);
      t.classList.toggle('frozen', game.perkActive('freeze'));
      // chefs are carrying this order, ready to serve: light the ticket up, one arc of the rim per carrier, ours first
      const cols = carriers.filter((c) => c.held.drink === g.recipe.id).map((c) => c.color);
      const sig = cols.join();
      if (t._deliver !== sig) {
        t._deliver = sig;
        t.classList.toggle('deliver', !!cols.length);
        if (cols.length) t.style.setProperty('--rimstops', rimStops(cols.length === 1 ? [cols[0], cols[0]] : cols));
      }
    }
    // world markers
    this.used.clear();
    const tmp = [0, 0, 0];
    for (const s of game.stations) {
      const it = s.item;
      if (s.fire) {
        this._mark('fire' + s.x + '_' + s.y, [s.wx, 2.0, s.wz], 'fire', () => `<div class="mk-fire">🔥</div>`);
        if (s.fire.hp < 0.999) this._prog('fh' + s.x + '_' + s.y, [s.wx, 1.35, s.wz], 1 - s.fire.hp, 'ext', 'Extinguishing');
        continue;
      }
      if (it && it.kind === 'vessel' && (it.contents.length || it.burnt)) {
        const warn = it.app === 'kettle' && it.over > 4 && !it.burnt;
        const state = it.burnt ? 'burnt' : it.result ? (it.result === 'sludge' ? 'sludge' : 'done') : 'brew';
        const onBase = it.holder && (it.holder.kind === 'stove' || it.holder.kind === 'blender');
        const sg = it.contents.join() + state + (warn ? 'w' : '') + (state === 'done' ? it.result : '');
        const el = this._mark('v' + it.uid, [s.wx, 1.75, s.wz], sg, () => {
          const icons = it.contents.map((id) => this.ingIcon(id)).join('');
          let top = '';
          if (state === 'done') top = `<div class="mk-done">${this.img('drink:' + it.result, 'ic')}</div>`;
          if (state === 'sludge') top = `<div class="mk-bad">✖ Murky Sludge · take to Zapper</div>`;
          if (state === 'burnt') top = `<div class="mk-bad">Burnt! · take to Zapper</div>`;
          return `${top}<div class="mk-ings">${icons}</div>${warn ? '<div class="mk-warn">!</div>' : ''}`;
        });
        if (warn) el.classList.toggle('blink', Math.sin(game.time * 14) > 0);
        if (state === 'brew' && it.waiting) {
          const miss = it.missing;
          const one = miss.length === 1 && miss[0].missing.length === 1 ? INGREDIENTS[miss[0].missing[0]].name : null;
          this._prog('vb' + it.uid, [s.wx, 1.75, s.wz], 1, 'brew', `${one ? 'Add ' + one + ' to finish' : 'Missing an ingredient'}`);
        } else if (state === 'brew') {
          const verb = it.app === 'kettle' ? 'Brewing' : 'Blending';
          this._prog('vb' + it.uid, [s.wx, 1.75, s.wz], it.progress, 'brew', onBase ? verb : 'Paused · put back on ' + (it.app === 'kettle' ? 'stove' : 'base'));
        } else if (state === 'done' && it.app === 'kettle' && onBase && it.over > 1) {
          this._prog('vo' + it.uid, [s.wx, 1.75, s.wz], Math.min(1, it.over / 11), 'burn', it.over > 4 ? 'Boiling over!' : 'Ready · take it off soon');
        }
      } else if (it && it.kind === 'ing' && it.needsPrep && (it.prog > 0 || (s.kind === 'chop' && it.def.prep === 'chop') || (s.kind === 'grind' && it.def.prep === 'grind'))) {
        this._prog('p' + it.uid, [s.wx, 1.35, s.wz], it.prog, 'prep', it.def.prep === 'chop' ? 'Chopping' : 'Grinding');
      } else if (it && it.kind === 'cup' && s.kind === 'carbonator' && it.drink && !it.fizzy) {
        this._prog('c' + it.uid, [s.wx, 1.9, s.wz], it.fizzProg, 'fizz', 'Carbonating');
      } else if (s.kind === 'sink' && s.dirty > 0) {
        this._mark('s' + s.x + '_' + s.y, [s.wx, 1.7, s.wz], 'sink' + s.dirty, () => `<div class="mk-count">${s.dirty} dirty</div>`);
        this._prog('sp' + s.x + '_' + s.y, [s.wx, 1.3, s.wz], s.prog, 'wash', 'Washing');
      } else if (s.kind === 'rack') {
        this._mark('r' + s.x + '_' + s.y, [s.wx, 1.6, s.wz], 'rack' + s.clean, () => `<div class="mk-count cup ${s.clean ? '' : 'empty'}">${this.img('cup', 'ic')}${s.clean}</div>`);
      }
    }
    // held ingredients mid-prep keep their progress visible
    for (const c of game.chefs) {
      const it = c.held;
      if (it && it.kind === 'ing' && it.needsPrep && it.prog > 0 && c.respawn <= 0) this._prog('p' + it.uid, [c.x, 1.9, c.z], it.prog, 'prep', 'Half ' + (it.def.prep === 'chop' ? 'chopped' : 'ground'));
      if (c.respawn > 0) this._prog('rs' + c.index, [c.spawn[0], 1.2, c.spawn[1]], 1 - c.respawn / 3, 'respawn', `${c.name} respawning`);
    }
    // bridges: time until they move
    const ev = game.events;
    if (game.twist === 'bridge' && game.state === 'play' && game.world.bridges.length) {
      const bset = game.world.bridges;
      const mid = bset.reduce((a, b) => [a[0] + b.wx / bset.length, a[1] + b.wz / bset.length], [0, 0]);
      for (const side of [-1, 1]) {
        const bs = bset.filter((b) => b.side === side);
        if (!bs.length) continue;
        const cx = bs.reduce((a, b) => a + b.homeX, 0) / bs.length;
        if (ev.bridgePhase === 'ext' && ev.bridgeT < 6) this._prog('br' + side, [cx, 0.9, mid[1]], 1 - ev.bridgeT / 6, 'burn', `Retracting in ${Math.ceil(ev.bridgeT)}s`);
        else if (ev.bridgePhase === 'ret') this._prog('br' + side, [cx, 0.9, mid[1]], 1 - ev.bridgeT / 9, 'brew', `Back in ${Math.ceil(ev.bridgeT)}s`);
      }
    }
    // cups held/placed show contents
    const cupMark = (it, pos) => {
      if (it.kind !== 'cup' || !it.drink) return;
      this._mark('cup' + it.uid, [pos[0], pos[1] + 0.55, pos[2]], it.drink + it.fizzy, () => `<div class="mk-cup${it.drink === 'sludge' ? ' sludge' : ''}">${this.img('drink:' + it.drink + (RECIPES[it.drink] && RECIPES[it.drink].fizz && !it.fizzy ? ':flat' : ''), 'ic')}${it.fizzy ? this.img('fizz', 'ic sm') : ''}${it.drink === 'sludge' ? '<span>Sludge</span>' : ''}</div>`);
    };
    for (const c of game.chefs) if (c.held && c.respawn <= 0) cupMark(c.held, c.held.node.worldPos(tmp));
    for (const s of game.stations) if (s.item && s.kind !== 'carbonator') cupMark(s.item, s.item.node.worldPos(tmp));
    // target preview label & coach arrows
    for (const c of game.chefs) {
      if (c.selPos && c.preview && game.state === 'play') {
        const pv = c.preview;
        this._mark('pv' + c.index, [c.selPos[0], c.selPos[1], c.selPos[2]], pv.text + pv.bad, () => `<div class="mk-preview${pv.bad ? ' bad' : ''}" style="--c:${c.color}">${pv.text}</div>`);
      }
      const ht = c.hint && c.hint.target;
      if (ht && game.state === 'play' && !(c.target && c.target.station === ht)) {
        this._mark('arrow' + c.index, [ht.wx, ht.node.position[1] + 0.9, ht.wz], 'arrow' + c.color, () => `<div class="mk-arrow" style="--c:${c.color}">▼</div>`);
      }
    }
    if (game.state === 'play') this._coach(game);
    this._delegateBar(game);
    const w = game.wheel;
    if (w) {
      const el = this._mark('wheel', [w.anchor.x, 1.1, w.anchor.z], 'w' + w.tasks.map((t) => t.id).join(), () => this._wheelHtml(game, w));
      el.style.zIndex = 5;
      this._wheelState(el, game, w);
    }
    // chef tags
    for (const c of game.chefs) {
      if (c.respawn > 0) continue;
      if (c.isBot) {
        // while the Delegate wheel is open: its owner's bots light up (a ring in their colour), the rest fade back
        const mine = w && game.botOwner[c.index] === w.owner, other = w && !mine;
        if (game.state === 'play' || game.state === 'countdown') {
          const el = this._mark('think' + c.index, [c.x, 2.0 + (c.held ? 0.15 : 0), c.z], 'th' + c.thought, () => this._thoughtHtml(c));
          el.classList.toggle('mk-mine', !!mine);
          el.classList.toggle('mk-theirs', !!other);
          if (mine) el.style.setProperty('--own', w.anchor.color);
        }
        if (mine) this._mark('own' + c.index, [c.x, 0.06, c.z], 'own' + w.anchor.color, () => `<div class="mk-own" style="--c:${w.anchor.color}"><i></i><b>yours</b></div>`);
        continue;
      }
      const label = game.solo ? (c.active ? '▼' : '') : 'P' + (c.index + 1);
      if (!label) continue;
      this._mark('chef' + c.index, [c.x, 1.95 + (c.held ? 0.15 : 0), c.z], label, () => `<div class="mk-chef" style="--c:${c.color}">${label}</div>`);
    }
    // the wheel just closed: show the choice landing, then let it shrink away (below)
    if (!w && game.wheelClosed) {
      const m = this.markers.get('wheel');
      if (m) {
        this._wheelState(m.el, game, game.wheelClosed);
        const sel = game.wheelClosed.sel;
        if (game.wheelClosed.chose) for (const e of [m.el.querySelectorAll('.wseg')[sel], m.el.querySelectorAll('.wg')[sel]]) e && e.classList.add('chosen');
        m.el.classList.add('closing');
      }
      game.wheelClosed = null;
    }
    // cleanup: whatever's no longer shown fades out rather than blinking away
    for (const [k, m] of this.markers) {
      if (!this.used.has(k)) {
        const el = m.el;
        el.classList.add('mk-out');
        setTimeout(() => el.remove(), el.classList.contains('closing') ? 320 : 200);
        this.markers.delete(k);
      }
    }
  }
}